import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    TextField,
    Box,
    Chip,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
    Autocomplete,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Divider,
    MenuItem,
    DialogContentText,
    Grid,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { debounce } from 'lodash';

const US_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

const AdminSubscriptions = () => {
    // General states
    const [subscriptions, setSubscriptions] = useState([]);
    const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Dialog states
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);

    // Manual subscription creation states

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const [createLoading, setCreateLoading] = useState(false);
    const [userMode, setUserMode] = useState('new');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [extendNote, setExtendNote] = useState('');
    const [extendLoading, setExtendLoading] = useState(false);
    const [openExtendDialog, setOpenExtendDialog] = useState(false);
    const [subscriptionToExtend, setSubscriptionToExtend] = useState(null);

    const [newUserData, setNewUserData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
    });
    const [formErrors, setFormErrors] = useState({
        phone: '',
        zip: '',
    });

    const fetchSubscriptions = useCallback(async (pageNum = 1, search = '') => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/subscriptions`, {
                params: {
                    page: pageNum,
                    limit: 20,
                    search: search,
                },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            const { data, totalPages } = response.data;

            // Transform the data to ensure proper date handling
            const transformedData = Array.isArray(data)
                ? data.map((subscription) => ({
                      ...subscription,
                      isSubscriptionActive: new Date(subscription.endDate) > new Date(),
                  }))
                : [];

            if (pageNum === 1) {
                setSubscriptions(transformedData);
            } else {
                setSubscriptions((prev) => [...prev, ...transformedData]);
            }

            setTotalPages(totalPages);
            setHasMore(pageNum < totalPages);
            setPage(pageNum);
            setError(null);
        } catch (err) {
            console.error('Error fetching subscriptions:', err);
            setError('Failed to fetch subscriptions. Please try again.');
            setSubscriptions([]);
        } finally {
            setLoading(false);
            setSearching(false);
        }
    }, []);

    // Then define debouncedSearch after fetchSubscriptions
    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            setSearching(true);
            fetchSubscriptions(1, searchValue);
        }, 500),
        [fetchSubscriptions],
    );

    // Handle search input change
    const handleSearchChange = useCallback(
        (event) => {
            const value = event.target.value;
            setSearchTerm(value);
            setPage(1);
            debouncedSearch(value);
        },
        [debouncedSearch],
    );

    // Debounced user search for subscription creation dialog
    const debouncedUserSearch = useCallback(
        debounce((searchValue) => {
            fetchUsers(searchValue);
        }, 300),
        [],
    );

    // Initial fetch effect
    useEffect(() => {
        fetchSubscriptions(1);
    }, [fetchSubscriptions]);

    // Update the load more handler
    const handleLoadMore = () => {
        if (!loading && hasMore) {
            fetchSubscriptions(page + 1, searchTerm);
        }
    };

    // Update the reset handler
    const handleReset = () => {
        setSearchTerm('');
        setPage(1);
        fetchSubscriptions(1, '');
    };

    const validatePhone = (phone) => {
        if (!phone) return true; // Optional field
        return /^\d{10}$|^\d{3}-\d{3}-\d{4}$/.test(phone);
    };

    const validateZip = (zip) => {
        if (!zip) return true; // Optional field
        return /^\d{5}$/.test(zip);
    };
    // Handle phone input with formatting
    const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/[^\d-]/g, '');

        // Handle backspace on formatted number
        if (value.length === 9 && !value.includes('-')) {
            value = value.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3');
        }

        // Auto-format as user types
        if (value.length === 10 && !value.includes('-')) {
            value = value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }

        setNewUserData({ ...newUserData, phone: value });
        setFormErrors({
            ...formErrors,
            phone: validatePhone(value) ? '' : 'Please enter a valid 10-digit phone number',
        });
    };

    // Handle zip input
    const handleZipChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 5);
        setNewUserData({ ...newUserData, zip: value });
        setFormErrors({
            ...formErrors,
            zip: validateZip(value) ? '' : 'Please enter a valid 5-digit ZIP code',
        });
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loadingProducts, setLoadingProducts] = useState(false);

    // Filter subscriptions based on search term
    const filterSubscriptions = useCallback(() => {
        let filtered = subscriptions;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = subscriptions.filter((subscription) => {
                if (!subscription) return false;

                const subscriptionId = (subscription._id || '').toLowerCase();
                const firstName = (subscription.user?.firstName || '').toLowerCase();
                const lastName = (subscription.user?.lastName || '').toLowerCase();
                const fullName = `${firstName} ${lastName}`.toLowerCase();
                const email = (subscription.user?.email || '').toLowerCase();
                const orderId = (subscription.orderId || '').toLowerCase();
                const products = (subscription.product || []).map((p) => (p?.name || '').toLowerCase()).join(' ');

                return (
                    subscriptionId.includes(searchLower) ||
                    firstName.includes(searchLower) ||
                    lastName.includes(searchLower) ||
                    fullName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    orderId.includes(searchLower) ||
                    products.includes(searchLower)
                );
            });
        }

        // Remove the sort here since it's now handled by the backend
        setFilteredSubscriptions(filtered);
    }, [searchTerm, subscriptions]);

    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            if (response.data?.status === 'success' && response.data?.data?.data) {
                // Transform products to ensure they have proper _id field
                const transformedProducts = response.data.data.data.map((product) => ({
                    _id: product._id || product.id,
                    name: product.name || 'Unnamed Product',
                    description: product.description || '',
                    price: product.price || 0,
                    allowedTests: product.allowedTests || [],
                }));
                setProducts(transformedProducts);
            } else {
                throw new Error('Invalid product data format');
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to load products');
        } finally {
            setLoadingProducts(false);
        }
    };

    const fetchUsers = async (search = '') => {
        try {
            setLoadingUsers(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: {
                    search: search,
                    limit: 500,
                },
            });

            // Handle the nested response structure
            const usersData = response.data?.data?.users || [];
            //console.log('Fetched users:', usersData); // For debugging
            setUsers(usersData);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    // Fetch users and products for manual subscription creation
    const fetchUsersAndProducts = async () => {
        try {
            const [usersResponse, testsResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/users`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    params: { search: '', limit: 500 },
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/products`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    params: { limit: 1000 },
                }),
            ]);

            // Handle users data - extract from nested response structure
            const usersData = usersResponse.data?.data?.users || [];
            setUsers(usersData);

            // Log raw products response
            console.log('Raw products response:', testsResponse.data);

            // Handle products data - check for nested structure
            let productsData = [];
            if (testsResponse.data?.data?.data && Array.isArray(testsResponse.data.data.data)) {
                productsData = testsResponse.data.data.data;
            } else if (Array.isArray(testsResponse.data)) {
                productsData = testsResponse.data;
            } else if (Array.isArray(testsResponse.data.products)) {
                productsData = testsResponse.data.products;
            } else if (testsResponse.data?.data && Array.isArray(testsResponse.data.data)) {
                productsData = testsResponse.data.data;
            }

            // Ensure proper _id field for products
            const transformedProducts = productsData.map((product) => ({
                _id: product._id || product.id,
                name: product.name || 'Unnamed Product',
                description: product.description || '',
                price: product.price || 0,
                allowedTests: product.allowedTests || [],
            }));

            console.log('Transformed products:', transformedProducts);
            setProducts(transformedProducts);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load users or products');
            setUsers([]);
            setProducts([]);
        }
    };
    // Effect hooks

    useEffect(() => {
        const initializeData = async () => {
            await Promise.all([fetchSubscriptions(1), fetchProducts(), fetchUsers()]);
        };

        initializeData();
    }, [fetchSubscriptions]);

    useEffect(() => {
        filterSubscriptions();
    }, [filterSubscriptions]);

    const isSubscriptionActive = (subscription) => {
        if (!subscription?.endDate) return false;
        return new Date(subscription.endDate) > new Date();
    };

    // Handle subscription extension
    const handleExtendSubscription = async () => {
        if (!subscriptionToExtend) return;
        try {
            setExtendLoading(true);
            await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/subscriptions/extendSubscription/${subscriptionToExtend._id}`,
                { note: extendNote },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                },
            );
            fetchSubscriptions(1);
            setOpenExtendDialog(false);
            setExtendNote('');
            setSubscriptionToExtend(null);
            setOpenEditDialog(false);
        } catch (err) {
            setError('Failed to extend subscription. Please try again.');
        } finally {
            setExtendLoading(false);
        }
    };

    // Handle manual subscription creation
    const handleCreateManualSubscription = async () => {
        try {
            setCreateLoading(true);
            setError(null);

            let userId = selectedUser?._id;
            let userPassword = null;

            // Create new user if needed
            if (userMode === 'new') {
                const userResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, newUserData, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                userId = userResponse.data.data.user._id;
                userPassword = newUserData.password;
            }

            // Extract product IDs instead of product names
            const productIds = selectedProducts.map((product) => product._id);

            // Send subscription request with user data
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/subscriptions/manual`,
                {
                    userId,
                    productIds: productIds,
                    duration: 180,
                    newUserCreated: userMode === 'new',
                    userPassword: userPassword,
                    newUserData: userMode === 'new' ? newUserData : null, // Include user data only for new users
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
            );

            console.log('Subscription created successfully:', response.data);

            setOpenCreateDialog(false);
            resetCreateForm();
            fetchSubscriptions(1);
        } catch (err) {
            console.error('Creation error:', err);
            setError(err.response?.data?.message || 'Failed to create subscription');
        } finally {
            setCreateLoading(false);
        }
    };

    const resetCreateForm = () => {
        setUserMode('new');
        setSelectedUser(null);
        setSelectedProducts([]);
        setNewUserData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zip: '',
        });
        setFormErrors({
            phone: '',
            zip: '',
        });
    };

    const handleDeleteSubscription = async () => {
        if (!subscriptionToDelete) return;

        try {
            setDeleteLoading(true);
            console.log('Deleting subscription:', subscriptionToDelete._id);

            const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/subscriptions/${subscriptionToDelete._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            if (response.status === 204) {
                // Force a fresh fetch from the server
                await fetchSubscriptions(1); // Reset to page 1 and fetch fresh data

                setOpenDeleteDialog(false);
                setError(null);
            }
        } catch (err) {
            console.error('Delete error:', err);
            setError('Failed to delete subscription. Please try again.');
        } finally {
            setDeleteLoading(false);
            setSubscriptionToDelete(null);
        }
    };

    const renderDeleteDialog = () => (
        <Dialog
            open={openDeleteDialog}
            onClose={() => !deleteLoading && setOpenDeleteDialog(false)}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
        >
            <DialogTitle id="delete-dialog-title">Confirm Subscription Deletion</DialogTitle>
            <DialogContent>
                <DialogContentText id="delete-dialog-description">
                    Are you sure you want to delete this subscription? This action cannot be undone.
                    {subscriptionToDelete && (
                        <>
                            <br />
                            <br />
                            <strong>User:</strong> {subscriptionToDelete.user?.firstName} {subscriptionToDelete.user?.lastName}
                            <br />
                            <strong>Email:</strong> {subscriptionToDelete.user?.email}
                            <br />
                            <strong>Products:</strong>{' '}
                            {(subscriptionToDelete.product || [])
                                .map((p) => p?.name)
                                .filter(Boolean)
                                .join(', ')}
                        </>
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleteLoading} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleDeleteSubscription}
                    disabled={deleteLoading}
                    color="error"
                    variant="contained"
                    startIcon={<DeleteIcon />}
                >
                    {deleteLoading ? 'Deleting...' : 'Delete Subscription'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Render functions
    const renderCreateDialog = () => (
        <Dialog open={openCreateDialog} onClose={() => !createLoading && setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Create Manual Subscription</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                    <RadioGroup row value={userMode} onChange={(e) => setUserMode(e.target.value)}>
                        <FormControlLabel value="new" control={<Radio />} label="New User" />
                        <FormControlLabel value="existing" control={<Radio />} label="Existing User" />
                    </RadioGroup>
                </FormControl>
                {userMode === 'existing' && (
                    <Autocomplete
                        value={selectedUser}
                        onChange={(_, newValue) => setSelectedUser(newValue)}
                        onInputChange={(_, newInputValue, reason) => {
                            if (reason === 'input') {
                                debouncedUserSearch(newInputValue);
                            }
                        }}
                        options={Array.isArray(users) ? users : []}
                        loading={loadingUsers}
                        getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName} (${option.email})` : '')}
                        isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            return option._id === value._id;
                        }}
                        filterOptions={(x) => x}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search User (type to search)"
                                required
                                sx={{ mb: 2 }}
                                error={!selectedUser && userMode === 'existing'}
                                helperText="Type a name or email to search all users"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
                {userMode === 'new' && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={newUserData.firstName}
                            onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={newUserData.lastName}
                            onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={newUserData.email}
                            onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            value={newUserData.password}
                            onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                            required
                            sx={{ mb: 2 }}
                            helperText="Password will be emailed to the user"
                        />
                        <TextField
                            fullWidth
                            label="Phone"
                            value={newUserData.phone}
                            onChange={handlePhoneChange}
                            sx={{ mb: 2 }}
                            error={!!formErrors.phone}
                            helperText={formErrors.phone || 'Format: 1234567890 or 123-456-7890'}
                        />
                        <TextField
                            fullWidth
                            label="Address"
                            value={newUserData.address}
                            onChange={(e) => setNewUserData({ ...newUserData, address: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="City"
                            value={newUserData.city}
                            onChange={(e) => setNewUserData({ ...newUserData, city: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            select
                            fullWidth
                            label="State"
                            value={newUserData.state}
                            onChange={(e) => setNewUserData({ ...newUserData, state: e.target.value })}
                            sx={{ mb: 2 }}
                        >
                            {US_STATES.map((state) => (
                                <MenuItem key={state.value} value={state.value}>
                                    {state.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            label="ZIP Code"
                            value={newUserData.zip}
                            onChange={handleZipChange}
                            sx={{ mb: 2 }}
                            error={!!formErrors.zip}
                            helperText={formErrors.zip || '5-digit ZIP code'}
                            inputProps={{ maxLength: 5 }}
                        />
                    </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Autocomplete
                    multiple
                    value={selectedProducts}
                    onChange={(_, newValue) => {
                        console.log('Product selection changed:', newValue);
                        setSelectedProducts(newValue);
                    }}
                    options={Array.isArray(products) ? products : []}
                    getOptionLabel={(option) => option ? `${option.name || ''} ($${option.price || ''})` : ''}
                    isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        return option._id === value._id;
                    }}
                    renderOption={(props, option) => (
                        <li {...props} key={option._id || Math.random().toString()}>
                            <Box>
                                <Typography variant="body1">{option.name || ''}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ${option.price || ''} - Test: {option.allowedTest?.name || 'N/A'}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    loading={loadingProducts}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Products"
                            required
                            error={selectedProducts.length === 0}
                            helperText={selectedProducts.length === 0 ? 'Please select at least one product' : ''}
                        />
                    )}
                />
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => {
                        setOpenCreateDialog(false);
                        resetCreateForm();
                    }}
                    disabled={createLoading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreateManualSubscription}
                    variant="contained"
                    disabled={
                        createLoading ||
                        (userMode === 'existing' && !selectedUser) ||
                        (userMode === 'new' && (!newUserData.email || !newUserData.password)) ||
                        !selectedProducts.length
                    }
                >
                    {createLoading ? 'Creating...' : 'Create Subscription'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderExtendDialog = () => (
        <Dialog open={openExtendDialog} onClose={() => !extendLoading && setOpenExtendDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Extend Subscription by 1 Month</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    This will add 30 days to the subscription for{' '}
                    {subscriptionToExtend?.user?.firstName} {subscriptionToExtend?.user?.lastName}.
                </DialogContentText>
                <TextField
                    fullWidth
                    label="Note (optional)"
                    placeholder="e.g., Extended per phone request"
                    value={extendNote}
                    onChange={(e) => setExtendNote(e.target.value)}
                    multiline
                    rows={2}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { setOpenExtendDialog(false); setExtendNote(''); }} disabled={extendLoading}>
                    Cancel
                </Button>
                <Button onClick={handleExtendSubscription} variant="contained" disabled={extendLoading}>
                    {extendLoading ? 'Extending...' : 'Extend 30 Days'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // In AdminSubscriptions.js - Update the renderEditDialog function

    const renderEditDialog = () => (
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>Subscription Details</DialogTitle>
            <DialogContent>
                {selectedSubscription && (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Subscription Information
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>Subscription ID:</strong> {selectedSubscription._id || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>Order ID:</strong> {selectedSubscription.orderId || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>Start Date:</strong>{' '}
                                    {selectedSubscription.startDate
                                        ? new Date(selectedSubscription.startDate).toLocaleDateString()
                                        : 'No date'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>End Date:</strong>{' '}
                                    {selectedSubscription.endDate ? new Date(selectedSubscription.endDate).toLocaleDateString() : 'No date'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography>
                                    <strong>Status: </strong>
                                    <Chip
                                        label={new Date(selectedSubscription.endDate) > new Date() ? 'Active' : 'Expired'}
                                        color={new Date(selectedSubscription.endDate) > new Date() ? 'success' : 'error'}
                                        size="small"
                                    />
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography>
                                    <strong>Product(s):</strong>{' '}
                                    {(selectedSubscription.product || [])
                                        .map((p) => p?.name)
                                        .filter(Boolean)
                                        .join(', ') || 'No products'}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h6" gutterBottom>
                            User Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>Name:</strong>{' '}
                                    {selectedSubscription.user
                                        ? `${selectedSubscription.user.firstName || ''} ${selectedSubscription.user.lastName || ''}`
                                        : 'Unknown User'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>Email:</strong> {selectedSubscription.user?.email || 'No email'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <strong>Phone:</strong> {selectedSubscription.user?.phone || 'Not provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography>
                                    <strong>Address:</strong> {selectedSubscription.user?.address || 'Not provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography>
                                    <strong>City:</strong> {selectedSubscription.user?.city || 'Not provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography>
                                    <strong>State:</strong> {selectedSubscription.user?.state || 'Not provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography>
                                    <strong>ZIP:</strong> {selectedSubscription.user?.zip || 'Not provided'}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setSubscriptionToExtend(selectedSubscription);
                                    setOpenExtendDialog(true);
                                }}
                                variant="contained"
                                color="primary"
                                fullWidth
                            >
                                Extend Subscription by 1 Month
                            </Button>
                        </Box>

                        {selectedSubscription.renewalLog && selectedSubscription.renewalLog.length > 0 && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" gutterBottom>
                                    Renewal History
                                </Typography>
                                {selectedSubscription.renewalLog
                                    .slice()
                                    .sort((a, b) => new Date(b.extendedAt) - new Date(a.extendedAt))
                                    .map((entry, index) => (
                                        <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="body2">
                                                <strong>+{entry.daysAdded} days</strong> by {entry.extendedBy} on{' '}
                                                {new Date(entry.extendedAt).toLocaleDateString()}
                                            </Typography>
                                            {entry.note && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {entry.note}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenEditDialog(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 2 }}>
            <Typography variant="h4" gutterBottom>
                Subscription Management
            </Typography>

            <Button variant="contained" onClick={() => setOpenCreateDialog(true)} startIcon={<AddIcon />} sx={{ mb: 3 }}>
                Create Manual Subscription
            </Button>

            <Box sx={{ display: 'flex', mb: 2, gap: 2, alignItems: 'center' }}>
                <TextField
                    label="Search by name, email, ID, or products"
                    variant="outlined"
                    onChange={handleSearchChange}
                    value={searchTerm}
                    fullWidth
                    InputProps={{
                        startAdornment: <SearchIcon color="action" />,
                    }}
                />
                <Tooltip title="Refresh">
                    <IconButton onClick={() => fetchSubscriptions(1)}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && filteredSubscriptions.length === 0 ? (
                <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 2 }} />
            ) : filteredSubscriptions.length === 0 ? (
                <Typography>No subscriptions found. Try adjusting your search or refreshing the data.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Products</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subscriptions.map((subscription) => (
                                <TableRow key={subscription?._id || 'unknown'}>
                                    <TableCell>
                                        {subscription?.user
                                            ? `${subscription.user.firstName || ''} ${subscription.user.lastName || ''}`
                                            : 'Unknown User'}
                                    </TableCell>
                                    <TableCell>{subscription?.user?.email || 'No email'}</TableCell>
                                    <TableCell>
                                        {(subscription?.product || [])
                                            .map((p) => p?.name)
                                            .filter(Boolean)
                                            .join(', ') || 'No products'}
                                    </TableCell>
                                    <TableCell>
                                        {subscription?.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'No date'}
                                    </TableCell>
                                    <TableCell>
                                        {subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'No date'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={isSubscriptionActive(subscription) ? 'Active' : 'Expired'}
                                            color={isSubscriptionActive(subscription) ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedSubscription(subscription);
                                                        setOpenEditDialog(true);
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    onClick={() => {
                                                        setSubscriptionToDelete(subscription);
                                                        setOpenDeleteDialog(true);
                                                    }}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {hasMore && filteredSubscriptions.length === subscriptions.length && (
                <Button onClick={() => fetchSubscriptions(page + 1)} disabled={loading} sx={{ mt: 2 }}>
                    {loading ? 'Loading...' : 'Load More Subscriptions'}
                </Button>
            )}

            {renderCreateDialog()}

            {renderEditDialog()}

            {renderExtendDialog()}

            {renderDeleteDialog()}
        </Box>
    );
};

export default AdminSubscriptions;
